import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { TabButton, TabGroup } from "./ui/Tabs";
import {
  isUsernameAvailable,
  signInWithUsername,
  signUpWithUsername,
  type AuthIdentity,
} from "../lib/auth";
import { getThemeColors } from "../lib/theme";

type UsernameMode = "signin" | "signup";
type Availability = "idle" | "checking" | "available" | "taken";

const USERNAME_PATTERN = /^[a-zA-Z0-9_.]+$/;

export function UsernameForm({
  identity,
  isDark,
  pending,
  onSubmit,
}: {
  identity: AuthIdentity;
  isDark: boolean;
  pending: string | null;
  onSubmit: (
    key: string,
    action: () => Promise<AuthIdentity>,
  ) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const theme = getThemeColors(isDark);
  const [mode, setMode] = useState<UsernameMode>(
    identity.isAnonymous ? "signup" : "signin",
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [availabilityResult, setAvailabilityResult] = useState<{
    username: string;
    status: "available" | "taken" | "idle";
  } | null>(null);

  useEffect(() => {
    const trimmed = username.trim();
    if (
      mode !== "signup" ||
      trimmed.length < 3 ||
      !USERNAME_PATTERN.test(trimmed)
    ) {
      return;
    }
    const timer = setTimeout(() => {
      void isUsernameAvailable(trimmed)
        .then((available) =>
          setAvailabilityResult({
            username: trimmed,
            status: available ? "available" : "taken",
          }),
        )
        .catch(() =>
          setAvailabilityResult({ username: trimmed, status: "idle" }),
        );
    }, 400);
    return () => clearTimeout(timer);
  }, [username, mode]);

  const trimmedUsername = username.trim();
  const isCheckable =
    mode === "signup" &&
    trimmedUsername.length >= 3 &&
    USERNAME_PATTERN.test(trimmedUsername);
  const availability: Availability = !isCheckable
    ? "idle"
    : availabilityResult?.username === trimmedUsername
      ? availabilityResult.status
      : "checking";

  const usernameValid =
    username.trim().length >= 3 && USERNAME_PATTERN.test(username.trim());
  const passwordsMatch =
    mode === "signin" || password === "" || password === confirmPassword;

  const submit = () => {
    if (!usernameValid || !passwordsMatch || pending !== null) return;
    void (mode === "signup"
      ? onSubmit("username-signup", () =>
          signUpWithUsername(username.trim(), password),
        )
      : onSubmit("username-signin", () =>
          signInWithUsername(username.trim(), password),
        ));
  };

  return (
    <div>
      <TabGroup theme={theme}>
        <TabButton
          data-testid="username-mode-signup-btn"
          active={mode === "signup"}
          onClick={() => setMode("signup")}
          isDark={isDark}
          theme={theme}
          className="flex-1"
        >
          {t("account.username.signUpMode")}
        </TabButton>
        <TabButton
          data-testid="username-mode-signin-btn"
          active={mode === "signin"}
          onClick={() => setMode("signin")}
          isDark={isDark}
          theme={theme}
          className="flex-1"
        >
          {t("account.username.signInMode")}
        </TabButton>
      </TabGroup>

      <div
        className={`mt-4 overflow-hidden rounded-xl border ${theme.border} ${theme.bgSecondary}`}
      >
        <Input
          data-testid="username-input"
          type="text"
          autoComplete="username"
          placeholder={t("account.username.username")}
          className={`w-full border-0 bg-transparent p-3.5 text-sm focus:ring-0 ${theme.inputText} ${theme.inputPlaceholder}`}
          value={username}
          disabled={pending !== null}
          onChange={(event) => setUsername(event.target.value)}
        />
        <Input
          data-testid="password-input"
          type="password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          placeholder={t("account.username.password")}
          className={`w-full border-0 border-t bg-transparent p-3.5 text-sm focus:ring-0 ${theme.border} ${theme.inputText} ${theme.inputPlaceholder}`}
          value={password}
          disabled={pending !== null}
          onChange={(event) => setPassword(event.target.value)}
        />
        {mode === "signup" && (
          <Input
            data-testid="confirm-password-input"
            type="password"
            autoComplete="new-password"
            placeholder={t("account.username.confirmPassword")}
            className={`w-full border-0 border-t bg-transparent p-3.5 text-sm focus:ring-0 ${theme.border} ${theme.inputText} ${theme.inputPlaceholder}`}
            value={confirmPassword}
            disabled={pending !== null}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        )}
      </div>
      {mode === "signup" && availability !== "idle" && (
        <p
          data-testid="username-availability"
          className={`mt-2 text-sm ${availability === "taken" ? theme.errorText : theme.textMuted}`}
        >
          {availability === "checking"
            ? t("account.username.checking")
            : availability === "available"
              ? t("account.username.usernameAvailable")
              : t("account.username.usernameTaken")}
        </p>
      )}
      {mode === "signup" && !passwordsMatch && (
        <p className={`mt-2 text-sm ${theme.errorText}`}>
          {t("account.username.passwordMismatch")}
        </p>
      )}
      <Button
        data-testid={
          mode === "signup" ? "signup-username-btn" : "signin-username-btn"
        }
        type="button"
        variant="primary"
        isDark={isDark}
        disabled={
          pending !== null || !usernameValid || !passwordsMatch || !password
        }
        onClick={submit}
        showArrow
        className="mt-4"
      >
        {mode === "signup"
          ? t("account.username.create")
          : t("account.username.signIn")}
      </Button>
    </div>
  );
}
